/**
 * Journey timeline — the readable default, with the snake kept as a second view.
 *
 * The serpentine is a good picture of a *short* journey: a dozen steps from
 * first visit to payout, where the curve carries the sequence and the gaps show
 * velocity. At 295 events it stops being a picture and becomes a maze — nodes
 * stranded at opposite edges of a 1,600px lobe, labels far from the line they
 * belong to, no way to find anything.
 *
 * So the default is a dense chronological rail: grouped by year and month,
 * every row legible at a glance, full detail on hover, and type filters to cut
 * a long journey down to the part you care about. The snake stays one click
 * away and is the automatic choice when the journey is short enough to suit it.
 */

import { useMemo, useState } from "react";
import {
  AlertTriangle, CalendarDays, ChevronDown, List, Phone, User, Waypoints,
} from "lucide-react";

import { SnakeTimeline } from "@/components/timeline/SnakeTimeline";
import { StageLadder } from "@/components/timeline/StageLadder";
import { eventMeta } from "@/components/timeline/typeMeta";
import type { JourneyEvent } from "@/types/api";
import { inr } from "@/utils/format";

/** Short enough that the curve helps rather than hinders. */
const SNAKE_MAX = 24;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
                "August", "September", "October", "November", "December"];

function fullDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}
function timeOf(iso: string) {
  const d = new Date(iso);
  if (d.getHours() === 0 && d.getMinutes() === 0) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}
function daysBetween(a: string, b: string) {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);
}

export interface JourneyTimelineProps {
  events: JourneyEvent[];
  mode?: "client" | "lead" | "employee";
  /** Shown above the rail, e.g. the client or employee name. */
  subject?: string;
  loading?: boolean;
  /** Whether the journey currently ignores the period filter. */
  allTime?: boolean;
  /** Omit to hide the scope toggle entirely. */
  onAllTimeChange?: (next: boolean) => void;
}

export function JourneyTimeline({
  events, mode = "client", subject, loading, allTime, onAllTimeChange,
}: JourneyTimelineProps) {
  const [view, setView] = useState<"rail" | "flow" | null>(null);
  const [hidden, setHidden] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [monthsOpen, setMonthsOpen] = useState<Set<string>>(new Set());

  const visible = useMemo(
    () => events.filter((e) => !hidden.has(e.stage ?? 3)),
    [events, hidden],
  );

  // The curve is the point of this screen and now holds up at any size, so it
  // is the default. The list stays a click away for scanning or exporting.
  const effectiveView = view ?? "flow";

  const stageCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const e of events) {
      const n = e.stage ?? 3;
      counts.set(n, (counts.get(n) ?? 0) + 1);
    }
    return counts;
  }, [events]);

  /** year -> month -> events, newest first, so recent work is at the top. */
  const grouped = useMemo(() => {
    const out = new Map<number, Map<number, JourneyEvent[]>>();
    for (const e of [...visible].reverse()) {
      const d = new Date(e.timestamp);
      const y = d.getFullYear();
      const m = d.getMonth();
      if (!out.has(y)) out.set(y, new Map());
      const months = out.get(y)!;
      if (!months.has(m)) months.set(m, []);
      months.get(m)!.push(e);
    }
    return out;
  }, [visible]);

  const span = useMemo(() => {
    if (events.length === 0) return null;
    const first = events[0]!.timestamp;
    const last = events[events.length - 1]!.timestamp;
    return { first, last, days: daysBetween(first, last) };
  }, [events]);

  const totals = useMemo(() => {
    const revenue = events
      .filter((e) => e.entityType === "billing")
      .reduce((sum, e) => sum + (e.value ?? 0), 0);
    const commission = events
      .filter((e) => e.entityType === "conversion")
      .reduce((sum, e) => sum + (e.value ?? 0), 0);
    return { revenue, commission };
  }, [events]);

  if (loading) return <div className="skeleton h-96" />;

  if (events.length === 0) {
    return (
      <div className="card p-10 text-center">
        <Waypoints size={22} className="mx-auto text-ink-faint mb-2" />
        <p className="text-sm text-ink-muted">No journey events for this record.</p>
        <p className="text-xs text-ink-faint mt-2 max-w-md mx-auto">
          Either none were recorded, or the activities could not be linked —
          roughly a third of activities carry no client reference.
        </p>
      </div>
    );
  }

  const toggleStage = (n: number) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(n) ? next.delete(n) : next.add(n);
      return next;
    });

  return (
    <div className="space-y-3">
      {/* ---- header: what this journey is, and how to cut it down ---- */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="label">{mode} journey{subject ? ` · ${subject}` : ""}</div>
            <p className="text-[13.5px] text-ink-soft mt-1">
              <strong>{events.length}</strong> events
              {span && (
                <> from <strong>{fullDate(span.first)}</strong> to{" "}
                  <strong>{fullDate(span.last)}</strong>
                  <span className="text-ink-faint"> ({span.days} days)</span></>
              )}
              {totals.revenue > 0 && (
                <> · {inr(totals.revenue)} billed</>
              )}
              {totals.commission > 0 && (
                <> · {inr(totals.commission)} commission approved</>
              )}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
          {onAllTimeChange && (
            <div className="flex items-center gap-1 bg-surface-alt rounded-md p-0.5">
              {([[false, "Selected period"], [true, "All time"]] as const).map(
                ([value, text]) => (
                  <button
                    key={text}
                    onClick={() => onAllTimeChange(value)}
                    className={`px-2.5 py-1 rounded text-[12.5px] ${
                      Boolean(allTime) === value
                        ? "bg-surface shadow-card font-medium text-ink"
                        : "text-ink-muted hover:text-ink"}`}
                    title={value
                      ? "Ignore the period filter and show the whole relationship"
                      : "Limit the journey to the period selected above"}
                  >
                    {text}
                  </button>
                ),
              )}
            </div>
          )}
          <div className="flex items-center gap-1 bg-surface-alt rounded-md p-0.5">
            {([["flow", Waypoints, "Flow"], ["rail", List, "List"]] as const).map(
              ([key, Icon, text]) => (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12.5px] ${
                    effectiveView === key
                      ? "bg-surface shadow-card font-medium text-ink"
                      : "text-ink-muted hover:text-ink"
                  }`}
                  title={key === "flow"
                    ? "Curved flow — best for short journeys"
                    : "Chronological list — best for long journeys"}
                >
                  <Icon size={13} /> {text}
                </button>
              ),
            )}
          </div>
          </div>
        </div>

        <div className="pt-2.5 border-t border-rule">
          <StageLadder counts={stageCounts} hidden={hidden}
                       onToggle={toggleStage} onReset={() => setHidden(new Set())} />
        </div>
      </div>

      {effectiveView === "flow" ? (
        <>
          {visible.length > SNAKE_MAX && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-surface-alt
                            border border-rule text-[12.5px] text-ink-muted">
              <AlertTriangle size={13} className="mt-0.5 shrink-0 text-ink-faint" />
              <span>
                {visible.length} events — the curve runs long. Filter by type
                above to focus on part of the journey, or switch to List to scan
                and export.
              </span>
            </div>
          )}
          <SnakeTimeline events={visible} mode={mode} />
        </>
      ) : (
        <div className="card overflow-hidden">
          {[...grouped.entries()]
            .sort((a, b) => b[0] - a[0])
            .map(([year, months]) => (
              <section key={year}>
                {/* The year was missing entirely before, and these journeys
                    span multiple years — "17 Jan" alone is ambiguous. */}
                <header className="sticky top-0 z-10 flex items-center gap-3 px-4 py-2
                                   bg-ink text-white">
                  <CalendarDays size={14} />
                  <span className="font-display font-bold text-[15px] tracking-tight">
                    {year}
                  </span>
                  <span className="text-[12px] opacity-70 tnum">
                    {[...months.values()].reduce((n, l) => n + l.length, 0)} events
                  </span>
                </header>

                {[...months.entries()]
                  .sort((a, b) => b[0] - a[0])
                  .map(([month, list]) => {
                    const key = `${year}-${month}`;
                    const collapsed = monthsOpen.has(key);
                    return (
                      <div key={key}>
                        <button
                          onClick={() => setMonthsOpen((prev) => {
                            const next = new Set(prev);
                            next.has(key) ? next.delete(key) : next.add(key);
                            return next;
                          })}
                          className="w-full flex items-center gap-2 px-4 py-1.5
                                     bg-surface-alt border-y border-rule text-left"
                        >
                          <ChevronDown size={13}
                            className={`text-ink-faint transition-transform ${
                              collapsed ? "-rotate-90" : ""}`} />
                          <span className="label">{MONTHS[month]} {year}</span>
                          <span className="text-[11.5px] text-ink-faint tnum ml-auto">
                            {list.length}
                          </span>
                        </button>

                        {!collapsed && list.map((event, i) => (
                          <EventRow
                            key={event.id}
                            event={event}
                            previous={list[i + 1]}
                            expanded={expanded === event.id}
                            onToggle={() =>
                              setExpanded(expanded === event.id ? null : event.id)}
                          />
                        ))}
                      </div>
                    );
                  })}
              </section>
            ))}
        </div>
      )}
    </div>
  );
}

/** One row on the rail. Hover shows the detail card; click pins it open. */
function EventRow({
  event, previous, expanded, onToggle,
}: {
  event: JourneyEvent;
  previous?: JourneyEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  const meta = eventMeta(event);
  const gap = previous ? daysBetween(previous.timestamp, event.timestamp) : 0;
  const time = timeOf(event.timestamp);
  const inferred = event.linkConfidence === "inferred";

  return (
    <div className="group relative border-b border-rule last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-surface-alt
                   focus-visible:outline focus-visible:outline-2 focus-visible:outline-petrol"
      >
        {/* date column — always carries the year via the section header */}
        <div className="w-[86px] shrink-0 pt-0.5">
          <div className="font-mono text-[11.5px] text-ink tnum">
            {new Date(event.timestamp).toLocaleDateString("en-IN",
              { day: "2-digit", month: "short" })}
          </div>
          {time && <div className="font-mono text-[10.5px] text-ink-faint tnum">{time}</div>}
        </div>

        {/* spine */}
        <div className="relative flex flex-col items-center shrink-0 self-stretch">
          <span
            className="w-7 h-7 rounded-full grid place-items-center shrink-0 z-10"
            style={{
              background: meta.bg,
              border: `${inferred ? "1.5px dashed" : "1.5px solid"} ${meta.color}`,
            }}
          >
            <meta.Icon size={13} style={{ color: meta.color }} />
          </span>
          <span className="w-px flex-1 bg-rule mt-1" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-display font-semibold text-[14px]">{event.title}</span>
            <span className="chip border-transparent px-1 py-0"
                  title={event.stageMeaning ?? meta.stage.meaning}
                  style={{ color: meta.color, background: meta.bg }}>
              {meta.stage.n}. {meta.label}
            </span>
            {inferred && (
              <span className="chip border-warn/30 bg-warn-soft text-warn px-1 py-0">
                inferred
              </span>
            )}
            {gap >= 2 && (
              <span className="text-[11px] text-ink-faint font-mono">
                +{gap}d since previous
              </span>
            )}
          </div>
          {event.description && (
            <div className="text-[12.5px] text-ink-muted truncate">{event.description}</div>
          )}
          <div className="flex flex-wrap gap-x-3 text-[11.5px] text-ink-faint mt-0.5">
            {event.employeeName && (
              <span className="inline-flex items-center gap-1">
                <User size={10} /> {event.employeeName}
              </span>
            )}
            {event.clientName && (
              <span className="inline-flex items-center gap-1">
                <Phone size={10} /> {event.clientName}
              </span>
            )}
            {event.leadId && <span className="font-mono">{event.leadId}</span>}
          </div>
        </div>

        {event.value != null && (
          <div className="shrink-0 text-right font-display font-semibold text-[14px] tnum"
               style={{ color: meta.color }}>
            {inr(event.value)}
          </div>
        )}
      </button>

      {/* Hover card. Pinned open by click, so it can be read and copied from. */}
      <div
        className={`px-4 pb-3 pl-[132px] ${
          expanded ? "block" : "hidden group-hover:block"
        }`}
      >
        <div className="rounded-md border border-rule bg-surface-alt p-3">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="label">details</span>
            {event.sourceTable && (
              <span className="font-mono text-[11px] text-ink-faint">
                {event.sourceTable} · {event.sourceId}
              </span>
            )}
          </div>
          <dl className="grid gap-x-5 gap-y-1.5"
              style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
            {Object.entries(event.metadata ?? {}).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 text-[12.5px]">
                <dt className="text-ink-faint shrink-0">{k}</dt>
                <dd className="text-ink font-medium text-right break-words">
                  {String(v ?? "—")}
                </dd>
              </div>
            ))}
          </dl>
          {Object.keys(event.metadata ?? {}).length === 0 && (
            <p className="text-[12.5px] text-ink-faint">No further detail recorded.</p>
          )}
        </div>
      </div>
    </div>
  );
}
